import React, { useEffect, useMemo, useState } from 'react';
import { getModulesStructure } from './DocsSidebar';
import { exportAsJSON, exportAsPDF } from '../utils/exportDocs';
import { resolveDocumentPortalStatus } from '../utils/docsPaths';

const MAX_SEARCH_RESULTS = 12;

function entryMatchesDocsType(entry, docsType) {
  if (entry.kind !== 'document') return false;
  return entry.source === docsType || entry.source === 'docs';
}

function entryIsProductFacing(entry) {
  // Prefer search-index visibility (rebuilt with public default; plans set internal).
  if (String(entry.visibility ?? '').toLowerCase() === 'internal') return false;
  return true;
}

function resolveSearchEntry(entry, externalManifest) {
  const portalStatus = resolveDocumentPortalStatus(entry, externalManifest);
  return {
    ...entry,
    ...portalStatus
  };
}

function entryMatchesQuery(entry, query) {
  const normalized = query.trim().toLowerCase();
  if (normalized.length < 2) return false;

  return [
    entry.title,
    entry.description,
    entry.path,
    entry.module,
    entry.category,
    ...(entry.tags ?? [])
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
}

const DocsHeader = ({ title, onSearch, onToggleSidebar, docsType, onSearchResultSelect }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchEntries, setSearchEntries] = useState([]);
  const [externalManifest, setExternalManifest] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadSearchIndex() {
      try {
        const response = await fetch('/generated/search-index.json');
        if (!response.ok) return;
        const payload = await response.json();
        if (mounted) {
          setSearchEntries(payload.entries ?? []);
        }
      } catch {
        if (mounted) {
          setSearchEntries([]);
        }
      }
    }

    async function loadExternalManifest() {
      try {
        const response = await fetch('/generated/external-markdown-manifest.json');
        if (!response.ok) return;
        const payload = await response.json();
        if (mounted) {
          setExternalManifest(payload);
        }
      } catch {
        if (mounted) {
          setExternalManifest(null);
        }
      }
    }

    loadSearchIndex();
    loadExternalManifest();
    return () => {
      mounted = false;
    };
  }, []);

  const searchResults = useMemo(() => {
    return searchEntries
      .filter((entry) => entryMatchesDocsType(entry, docsType))
      .filter((entry) => entry.kind !== 'document' || entryIsProductFacing(entry))
      .filter((entry) => entryMatchesQuery(entry, searchValue))
      .map((entry) => resolveSearchEntry(entry, externalManifest))
      .slice(0, MAX_SEARCH_RESULTS);
  }, [docsType, externalManifest, searchEntries, searchValue]);
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch(value);
  };

  const handleSearchResultSelect = (entry) => {
    if (!entry.portalPath) return;
    onSearchResultSelect(entry.portalPath);
    setSearchValue('');
    setIsSearchFocused(false);
    onSearch('');
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    try {
      const modules = getModulesStructure(docsType);
      await exportAsJSON(modules);
    } catch (error) {
      console.error('Error exporting JSON:', error);
      alert('Failed to export documentation as JSON. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const modules = getModulesStructure(docsType);
      await exportAsPDF(modules);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export documentation as PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onToggleSidebar}
            className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchValue}
              onChange={handleSearchChange}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              className="w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {isSearchFocused && searchResults.length > 0 && (
              <div className="absolute right-0 z-50 mt-2 w-96 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
                <ul className="max-h-96 overflow-auto py-1">
                  {searchResults.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleSearchResultSelect(entry);
                        }}
                        className="block w-full px-4 py-3 text-left hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-gray-900">{entry.title}</span>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            {entry.sourceLabel}
                          </span>
                        </div>
                        {entry.description && (
                          <p className="mt-1 line-clamp-2 text-xs text-gray-500">{entry.description}</p>
                        )}
                        <p className="mt-1 truncate text-xs text-gray-400">
                          {entry.portalPath ?? entry.unavailableReason}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportJSON}
              disabled={isExporting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              title="Download all documentation as JSON"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">JSON</span>
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              title="Download all documentation as PDF"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">PDF</span>
            </button>
            {isExporting && (
              <div className="flex items-center text-gray-600">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DocsHeader;

